document.addEventListener("DOMContentLoaded", () => {
  initMobileMenus();
  initDesktopSubmenus();
  initCartDirectLinkSync();
  initProductGalleries();
  initProductOptionPickers();
  initConditionalPersonalization();
  initProductForms();
});

document.addEventListener("shopify:section:load", (event) => {
  initMobileMenus(event.target);
  initDesktopSubmenus(event.target);
  initProductGalleries(event.target);
  initProductOptionPickers(event.target);
  initConditionalPersonalization(event.target);
  initProductForms(event.target);
});

function initDesktopSubmenus(root = document) {
  root.querySelectorAll("[data-submenu-toggle]").forEach((button) => {
    if (button.dataset.submenuReady === "true") return;

    const item = button.closest(".site-nav__item");
    const targetId = button.getAttribute("aria-controls");
    const submenu = targetId ? document.getElementById(targetId) : null;
    const nav = item?.closest(".site-nav");
    if (!item || !submenu || !nav) return;

    button.dataset.submenuReady = "true";

    const closeSubmenu = ({ restoreFocus = false } = {}) => {
      item.classList.remove("is-submenu-open");
      button.setAttribute("aria-expanded", "false");
      if (restoreFocus) button.focus({ preventScroll: true });
    };

    const openSubmenu = () => {
      nav.querySelectorAll("[data-submenu-toggle]").forEach((otherButton) => {
        if (otherButton === button) return;
        otherButton.closest(".site-nav__item")?.classList.remove("is-submenu-open");
        otherButton.setAttribute("aria-expanded", "false");
      });
      item.classList.add("is-submenu-open");
      button.setAttribute("aria-expanded", "true");
    };

    button.addEventListener("click", () => {
      const isOpen = button.getAttribute("aria-expanded") === "true";
      if (isOpen) closeSubmenu();
      else openSubmenu();
    });

    button.addEventListener("keydown", (event) => {
      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
      event.preventDefault();
      openSubmenu();
      const links = Array.from(submenu.querySelectorAll("a[href]"));
      const destination = event.key === "ArrowUp" ? links[links.length - 1] : links[0];
      destination?.focus({ preventScroll: true });
    });

    item.addEventListener("focusout", () => {
      window.requestAnimationFrame(() => {
        if (!item.contains(document.activeElement) && !item.matches(":hover")) closeSubmenu();
      });
    });

    document.addEventListener("click", (event) => {
      if (!item.contains(event.target)) closeSubmenu();
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && button.getAttribute("aria-expanded") === "true") {
        closeSubmenu({ restoreFocus: true });
      }
    });
  });
}

function initMobileMenus(root = document) {
  root.querySelectorAll("[data-mobile-toggle]").forEach((button) => {
    if (button.dataset.mobileMenuReady === "true") return;

    const targetId = button.getAttribute("aria-controls");
    const target = targetId ? document.getElementById(targetId) : null;
    if (!target) return;

    button.dataset.mobileMenuReady = "true";
    const desktopQuery = window.matchMedia("(min-width: 990px)");
    const reduceMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const focusableSelector = [
      "a[href]",
      "button:not([disabled])",
      "input:not([disabled])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      "[tabindex]:not([tabindex='-1'])",
    ].join(",");
    let resizeFrame = 0;
    let openFrame = 0;
    let closeTimer = 0;
    let previouslyFocused = null;

    const syncMenuTop = () => {
      const header = button.closest(".site-header");
      const headerBottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
      target.style.setProperty("--mobile-menu-top", `${headerBottom}px`);
    };

    const getDrawerFocusableItems = () => Array.from(target.querySelectorAll(focusableSelector)).filter((element) => (
      !element.hidden && element.getAttribute("aria-hidden") !== "true" && element.getClientRects().length > 0
    ));

    const getFocusableItems = () => {
      const drawerItems = getDrawerFocusableItems();
      return button.getClientRects().length > 0 ? [button, ...drawerItems] : drawerItems;
    };

    const closeMenu = ({ restoreFocus = false, desktopResize = false } = {}) => {
      if (button.getAttribute("aria-expanded") !== "true" && !target.classList.contains("is-open")) return;

      if (openFrame) window.cancelAnimationFrame(openFrame);
      openFrame = 0;
      const focusWasInside = target.contains(document.activeElement);
      button.setAttribute("aria-expanded", "false");
      button.setAttribute("aria-label", "Open menu");
      target.setAttribute("aria-hidden", "true");
      target.classList.remove("is-open");
      target.classList.add("is-closing");

      if (restoreFocus && !desktopResize && button.getClientRects().length > 0) {
        button.focus({ preventScroll: true });
      } else if (focusWasInside) {
        const safeTarget = desktopResize
          ? button.closest(".shopify-section")?.querySelector(".brand-lockup") || document.querySelector(".brand-lockup")
          : previouslyFocused;
        safeTarget?.focus?.({ preventScroll: true });
      }

      const finishClose = () => {
        window.clearTimeout(closeTimer);
        closeTimer = 0;
        target.hidden = true;
        target.classList.remove("is-closing");
        target.style.removeProperty("--mobile-menu-top");
        document.body.classList.remove("menu-open");
        previouslyFocused = null;
      };

      if (reduceMotionQuery.matches || desktopResize) finishClose();
      else closeTimer = window.setTimeout(finishClose, 320);
    };

    const openMenu = () => {
      if (desktopQuery.matches) return;
      window.clearTimeout(closeTimer);
      closeTimer = 0;
      previouslyFocused = document.activeElement;
      syncMenuTop();
      button.setAttribute("aria-expanded", "true");
      button.setAttribute("aria-label", "Close menu");
      target.hidden = false;
      target.setAttribute("aria-hidden", "false");
      target.classList.remove("is-closing");
      document.body.classList.add("menu-open");
      target.getBoundingClientRect();
      openFrame = window.requestAnimationFrame(() => {
        openFrame = 0;
        target.classList.add("is-open");
        getDrawerFocusableItems()[0]?.focus({ preventScroll: true });
      });
    };

    target.setAttribute("aria-hidden", target.hidden ? "true" : "false");

    button.addEventListener("click", () => {
      if (button.getAttribute("aria-expanded") === "true") closeMenu();
      else openMenu();
    });

    target.addEventListener("click", (event) => {
      if (event.target === target) closeMenu({ restoreFocus: true });
      else if (event.target.closest("[data-mobile-close]")) closeMenu({ restoreFocus: true });
      else if (event.target.closest("a")) closeMenu();
    });

    document.addEventListener("keydown", (event) => {
      if (event.key !== "Tab" || button.getAttribute("aria-expanded") !== "true") return;

      const focusableItems = getFocusableItems();
      if (!focusableItems.length) {
        event.preventDefault();
        button.focus({ preventScroll: true });
        return;
      }

      const currentIndex = focusableItems.indexOf(document.activeElement);
      const nextIndex = currentIndex < 0
        ? (event.shiftKey ? focusableItems.length - 1 : 0)
        : (currentIndex + (event.shiftKey ? -1 : 1) + focusableItems.length) % focusableItems.length;

      event.preventDefault();
      focusableItems[nextIndex].focus({ preventScroll: true });
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && button.getAttribute("aria-expanded") === "true") {
        closeMenu({ restoreFocus: true });
      }
    });

    window.addEventListener("resize", () => {
      if (resizeFrame) window.cancelAnimationFrame(resizeFrame);
      resizeFrame = window.requestAnimationFrame(() => {
        resizeFrame = 0;
        if (button.getAttribute("aria-expanded") !== "true") return;
        if (desktopQuery.matches) closeMenu({ desktopResize: true });
        else syncMenuTop();
      });
    }, { passive: true });
  });
}

function selectProductMedia(productPage, media = {}, { animate = true, scrollMobile = true } = {}) {
  if (!productPage) return;

  const mediaId = media.mediaId == null ? "" : String(media.mediaId);
  const featured = productPage.querySelector(".product-page__featured");
  const featuredImage = featured?.querySelector("img");
  const thumbnails = Array.from(productPage.querySelectorAll("[data-gallery-thumbnail]"));
  const matchingThumbnail = mediaId
    ? thumbnails.find((thumbnail) => thumbnail.dataset.mediaId === mediaId)
    : null;
  const imageSource = media.mediaUrl || matchingThumbnail?.dataset.gallerySrc || "";
  const imageAlt = media.mediaAlt || matchingThumbnail?.dataset.galleryAlt || "Product image";

  if (featuredImage && imageSource) {
    const applyImage = () => {
      featuredImage.removeAttribute("srcset");
      featuredImage.removeAttribute("sizes");
      featuredImage.src = imageSource;
      featuredImage.alt = imageAlt;
      featuredImage.style.opacity = "1";
      if (featured && mediaId) featured.dataset.featuredMediaId = mediaId;
    };

    window.clearTimeout(productPage._gallerySwapTimer);
    if (animate) {
      featuredImage.style.opacity = "0";
      productPage._gallerySwapTimer = window.setTimeout(applyImage, 120);
    } else {
      applyImage();
    }
  }

  if (mediaId) {
    thumbnails.forEach((thumbnail) => {
      const isActive = thumbnail.dataset.mediaId === mediaId;
      thumbnail.classList.toggle("is-active", isActive);
      thumbnail.setAttribute("aria-pressed", isActive ? "true" : "false");
    });
  }

  if (!scrollMobile || (!mediaId && !media.mediaUrl)) return;
  const track = productPage.querySelector(".product-page__mobile-track");
  const slides = Array.from(productPage.querySelectorAll(".product-page__mobile-slide"));
  const temporarySlide = slides.find((slide) => slide.dataset.temporaryMediaId);
  if (temporarySlide && temporarySlide.dataset.temporaryMediaId !== (mediaId || media.mediaUrl)) {
    const image = temporarySlide.querySelector("img");
    if (image) {
      image.setAttribute("src", temporarySlide.dataset.originalSrc || "");
      if (temporarySlide.dataset.originalSrcset) image.setAttribute("srcset", temporarySlide.dataset.originalSrcset);
      else image.removeAttribute("srcset");
      if (temporarySlide.dataset.originalSizes) image.setAttribute("sizes", temporarySlide.dataset.originalSizes);
      else image.removeAttribute("sizes");
      image.alt = temporarySlide.dataset.originalAlt || "Product image";
    }
    delete temporarySlide.dataset.temporaryMediaId;
  }

  let matchingSlide = mediaId
    ? slides.find((slide) => slide.dataset.mediaId === mediaId)
    : null;
  if (!matchingSlide && media.mediaUrl && slides[0]) {
    matchingSlide = slides[0];
    const image = matchingSlide.querySelector("img");
    if (image) {
      if (matchingSlide.dataset.originalSrc == null) {
        matchingSlide.dataset.originalSrc = image.getAttribute("src") || "";
        matchingSlide.dataset.originalSrcset = image.getAttribute("srcset") || "";
        matchingSlide.dataset.originalSizes = image.getAttribute("sizes") || "";
        matchingSlide.dataset.originalAlt = image.alt || "Product image";
      }
      matchingSlide.dataset.temporaryMediaId = mediaId || media.mediaUrl;
      image.removeAttribute("srcset");
      image.removeAttribute("sizes");
      image.src = media.mediaUrl;
      image.alt = media.mediaAlt || "Product image";
    }
  }
  if (!track || !matchingSlide) return;

  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const left = matchingSlide.offsetLeft - Math.max(0, (track.clientWidth - matchingSlide.offsetWidth) / 2);
  track.scrollTo({ left: Math.max(0, left), behavior: reducedMotion || !animate ? "auto" : "smooth" });
}

function initProductGalleries(root = document) {
  root.querySelectorAll(".product-page").forEach((productPage) => {
    if (productPage.dataset.galleryReady === "true") return;
    productPage.dataset.galleryReady = "true";

    const thumbnails = Array.from(productPage.querySelectorAll("[data-gallery-thumbnail]"));
    thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener("click", () => {
        selectProductMedia(productPage, {
          mediaId: thumbnail.dataset.mediaId,
          mediaUrl: thumbnail.dataset.gallerySrc,
          mediaAlt: thumbnail.dataset.galleryAlt,
        });
      });
    });

    const track = productPage.querySelector(".product-page__mobile-track");
    const slides = Array.from(productPage.querySelectorAll(".product-page__mobile-slide"));
    const dots = Array.from(productPage.querySelectorAll(".product-page__mobile-dots span"));
    const previousButton = productPage.querySelector("[data-gallery-previous]");
    const nextButton = productPage.querySelector("[data-gallery-next]");
    const position = productPage.querySelector("[data-gallery-position]");
    if (!track || slides.length < 2 || dots.length !== slides.length) return;

    let ticking = false;
    let activeIndex = 0;
    const scrollToSlide = (index) => {
      const nextIndex = Math.max(0, Math.min(slides.length - 1, index));
      const slide = slides[nextIndex];
      const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const left = slide.offsetLeft - Math.max(0, (track.clientWidth - slide.offsetWidth) / 2);
      track.scrollTo({ left: Math.max(0, left), behavior: reducedMotion ? "auto" : "smooth" });
    };
    const updateDots = () => {
      const trackCenter = track.scrollLeft + track.clientWidth / 2;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      slides.forEach((slide, index) => {
        const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
        const distance = Math.abs(slideCenter - trackCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      dots.forEach((dot, index) => dot.classList.toggle("is-active", index === closestIndex));
      activeIndex = closestIndex;
      if (position) position.textContent = `Image ${closestIndex + 1} of ${slides.length}`;
      if (previousButton) previousButton.disabled = closestIndex === 0;
      if (nextButton) nextButton.disabled = closestIndex === slides.length - 1;
      ticking = false;
    };

    const initialMediaId = productPage.querySelector(".product-page__featured")?.dataset.featuredMediaId;
    const initialSlideIndex = initialMediaId
      ? slides.findIndex((slide) => slide.dataset.mediaId === initialMediaId)
      : 0;
    activeIndex = Math.max(0, initialSlideIndex);
    dots[activeIndex]?.classList.add("is-active");
    previousButton?.addEventListener("click", () => scrollToSlide(activeIndex - 1));
    nextButton?.addEventListener("click", () => scrollToSlide(activeIndex + 1));
    updateDots();
    if (initialMediaId) {
      window.requestAnimationFrame(() => {
        selectProductMedia(productPage, { mediaId: initialMediaId }, { animate: false, scrollMobile: true });
      });
    }
    track.addEventListener("scroll", () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(updateDots);
      }
    }, { passive: true });
  });
}

function getActivePurchaseConfirmations(form) {
  return Array.from(form.querySelectorAll("[data-purchase-confirmation]"))
    .filter((panel) => panel.dataset.confirmationActive === "true");
}

function concealPurchaseConfirmations(form) {
  form.dataset.purchaseConfirmationRevealed = "false";
  form.querySelectorAll("[data-purchase-confirmation]").forEach((panel) => {
    panel.hidden = true;
    panel.dataset.confirmationRevealed = "false";
    const checkbox = panel.querySelector(
      "[data-purchase-confirmation-checkbox], [data-special-order-acknowledgment]",
    );
    if (checkbox) {
      checkbox.checked = false;
      checkbox.required = false;
      checkbox.disabled = true;
    }
  });
}

function revealPurchaseConfirmations(form, submitButton) {
  const panels = getActivePurchaseConfirmations(form);
  if (!panels.length || form.dataset.purchaseConfirmationRevealed === "true") return false;

  form.dataset.purchaseConfirmationRevealed = "true";
  panels.forEach((panel) => {
    panel.hidden = false;
    panel.dataset.confirmationRevealed = "true";
  });
  submitButton.textContent = submitButton.dataset.confirmLabel || "I understand — add to bag";

  const heading = panels[0].querySelector("strong");
  if (heading) {
    heading.tabIndex = -1;
    window.requestAnimationFrame(() => {
      heading.focus({ preventScroll: true });
      panels[0].scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  }
  return true;
}

function acceptPurchaseConfirmations(form) {
  getActivePurchaseConfirmations(form).forEach((panel) => {
    const checkbox = panel.querySelector(
      "[data-purchase-confirmation-checkbox], [data-special-order-acknowledgment]",
    );
    if (!checkbox) return;
    checkbox.disabled = false;
    checkbox.required = false;
    checkbox.checked = true;
  });
}

function syncSpecialOrderState(form, variant) {
  const notice = form.querySelector("[data-fulfillment-notice]");
  if (!notice) return false;

  const acknowledgment = notice.querySelector("[data-special-order-acknowledgment]");
  const fulfillmentProperty = notice.querySelector("[data-special-order-property]");
  const availabilitySummary = form.closest(".product-page__card")
    ?.querySelector("[data-product-availability-summary]");
  const hasSelectedVariant = Boolean(variant?.id);
  const isSpecialOrder = hasSelectedVariant && variant.specialOrder === true;
  notice.dataset.confirmationActive = isSpecialOrder ? "true" : "false";
  concealPurchaseConfirmations(form);

  if (acknowledgment) {
    acknowledgment.disabled = true;
    acknowledgment.required = false;
    acknowledgment.checked = false;
  }
  if (fulfillmentProperty) fulfillmentProperty.disabled = !isSpecialOrder;

  if (availabilitySummary && form.dataset.mixedAvailability === "true") {
    availabilitySummary.textContent = hasSelectedVariant
      ? (isSpecialOrder ? (form.dataset.outOfStockLabel || "Available to order") : "In stock — ready to prepare")
      : "In stock + regular-order choices";
  }

  return isSpecialOrder;
}

function initHighVariantProductOptionPickers(root = document) {
  root.querySelectorAll("[data-high-variant-picker]").forEach((picker) => {
    if (picker.dataset.highVariantReady === "true") return;

    const form = picker.closest("form");
    const variantInput = form?.querySelector("[data-variant-id]");
    const status = form?.querySelector("[data-variant-status]");
    const submitButton = form?.querySelector('button[type="submit"]');
    const productCard = form?.closest(".product-page__card");
    const productPage = form?.closest(".product-page");
    const priceContainer = productCard?.querySelector(".price");
    const initialPriceMarkup = priceContainer?.innerHTML || "";
    const isProductClosed = form?.dataset.productClosed === "true";
    const isProductAvailable = picker.dataset.productAvailable === "true";
    if (!form || !variantInput || !submitButton) return;

    picker.dataset.highVariantReady = "true";
    let requestController = null;
    let requestNumber = 0;
    let hasSynced = false;

    const labels = {
      add: submitButton.dataset.addLabel || "Add to bag",
      specialOrder: submitButton.dataset.specialOrderLabel || "Add special order to bag",
      choose: submitButton.dataset.chooseLabel || "Choose options",
      unavailable: submitButton.dataset.unavailableLabel || "Unavailable",
      closed: submitButton.dataset.closedLabel || "Ordering closed",
    };

    const getSelects = () => Array.from(picker.querySelectorAll("[data-product-option]"));

    const getSelectedOptionValueIds = (selects = getSelects()) => selects
      .map((select) => select.selectedOptions[0]?.dataset.optionValueId || "")
      .filter(Boolean);

    const getSelectedVariant = () => {
      const dataElement = picker.querySelector("[data-high-selected-variant]");
      if (!dataElement) return null;

      try {
        return JSON.parse(dataElement.textContent || "null");
      } catch (_) {
        return null;
      }
    };

    const updatePrice = (variant) => {
      const currentPrice = priceContainer?.querySelector(".price__current");
      if (!variant || !currentPrice || !priceContainer) return;
      currentPrice.textContent = variant.price;

      const prefix = priceContainer.querySelector(".price__prefix");
      if (prefix) prefix.hidden = true;

      let compare = priceContainer.querySelector(".price__compare");
      let saleNote = priceContainer.querySelector(".price__sale-note");
      if (variant.compareAtPrice) {
        if (!compare) {
          compare = document.createElement("span");
          compare.className = "price__compare";
          priceContainer.appendChild(compare);
        }
        compare.textContent = variant.compareAtPrice;
        compare.hidden = false;
        if (!saleNote) {
          saleNote = document.createElement("span");
          saleNote.className = "price__sale-note";
          saleNote.textContent = "Sale";
          priceContainer.appendChild(saleNote);
        }
        saleNote.hidden = false;
      } else {
        if (compare) compare.hidden = true;
        if (saleNote) saleNote.hidden = true;
      }
    };

    const resetPrice = () => {
      if (priceContainer && initialPriceMarkup) priceContainer.innerHTML = initialPriceMarkup;
    };

    const setSubmitState = (canSubmit, label) => {
      submitButton.dataset.canSubmit = canSubmit ? "true" : "false";
      submitButton.dataset.restingLabel = label;
      if (submitButton.dataset.busy === "true") return;
      submitButton.disabled = !canSubmit;
      submitButton.setAttribute("aria-disabled", canSubmit ? "false" : "true");
      submitButton.textContent = label;
    };

    const updateUrl = (selectedVariant, optionValueIds) => {
      if (!window.history?.replaceState) return;

      const url = new URL(window.location.href);
      url.searchParams.delete("section_id");
      if (selectedVariant?.id) {
        url.searchParams.set("variant", selectedVariant.id);
        url.searchParams.delete("option_values");
      } else {
        url.searchParams.delete("variant");
        if (optionValueIds.length > 0) {
          url.searchParams.set("option_values", optionValueIds.join(","));
        } else {
          url.searchParams.delete("option_values");
        }
      }
      window.history.replaceState({}, "", url);
    };

    const syncPicker = ({ updateHistory = false, animateMedia = false } = {}) => {
      const selects = getSelects();
      selects.forEach((select) => { select.required = true; });

      const selectedValues = selects.map((select) => select.value);
      const optionValueIds = getSelectedOptionValueIds(selects);
      const missingOptions = selects
        .filter((select) => !select.value)
        .map((select) => select.dataset.optionName || "option");
      const selectedVariant = missingOptions.length === 0 ? getSelectedVariant() : null;
      const isSpecialOrder = syncSpecialOrderState(form, selectedVariant);

      variantInput.value = selectedVariant?.id || "";

      if (isProductClosed) {
        setSubmitState(false, labels.closed);
        if (status) status.textContent = "Ordering closed";
      } else if (!isProductAvailable) {
        setSubmitState(false, labels.unavailable);
        if (status) status.textContent = "Currently unavailable";
      } else if (missingOptions.length > 0) {
        resetPrice();
        setSubmitState(false, labels.choose);
        if (status) status.textContent = `Choose ${missingOptions.join(", ")}`;
      } else if (!selectedVariant?.available) {
        setSubmitState(false, labels.unavailable);
        if (status) status.textContent = "This combination is unavailable";
      } else {
        setSubmitState(true, isSpecialOrder ? labels.specialOrder : labels.add);
        if (status) {
          status.textContent = isSpecialOrder
            ? `${selectedValues.join(" / ")} · ${form.dataset.outOfStockLabel || "Available to order"}`
            : `${selectedValues.join(" / ")}${form.dataset.mixedAvailability === "true" ? " · In stock" : ""}`;
        }
      }

      if (selectedVariant) {
        updatePrice(selectedVariant);
        if (selectedVariant.mediaId || selectedVariant.mediaUrl) {
          selectProductMedia(productPage, selectedVariant, {
            animate: animateMedia && hasSynced,
            scrollMobile: true,
          });
        }
      }

      if (updateHistory) updateUrl(selectedVariant, optionValueIds);
      hasSynced = true;
    };

    picker.addEventListener("change", async (event) => {
      const changedSelect = event.target.closest?.("[data-product-option]");
      if (!changedSelect || !picker.contains(changedSelect)) return;

      const currentSelects = getSelects();
      const changedIndex = currentSelects.indexOf(changedSelect);
      if (changedIndex < 0) return;

      currentSelects.slice(changedIndex + 1).forEach((select) => { select.value = ""; });
      const selectedByPosition = new Map(currentSelects.map((select) => [
        select.dataset.optionPosition || select.dataset.optionIndex,
        select.selectedOptions[0]?.dataset.optionValueId || "",
      ]));
      const optionValueIds = getSelectedOptionValueIds(currentSelects);
      const focusPosition = changedSelect.dataset.optionPosition || changedSelect.dataset.optionIndex;

      variantInput.value = "";
      setSubmitState(false, labels.choose);
      if (status) status.textContent = "Updating options…";
      picker.setAttribute("aria-busy", "true");

      requestController?.abort();
      requestController = new AbortController();
      const activeRequest = ++requestNumber;

      try {
        const requestUrl = new URL(picker.dataset.productUrl || window.location.pathname, window.location.origin);
        requestUrl.searchParams.set("section_id", picker.dataset.sectionId);
        requestUrl.searchParams.delete("variant");
        if (optionValueIds.length > 0) {
          requestUrl.searchParams.set("option_values", optionValueIds.join(","));
        } else {
          requestUrl.searchParams.delete("option_values");
        }

        const response = await fetch(requestUrl.toString(), {
          credentials: "same-origin",
          headers: { Accept: "text/html" },
          signal: requestController.signal,
        });
        if (!response.ok) throw new Error(`Option refresh failed with status ${response.status}`);

        const responseText = await response.text();
        if (activeRequest !== requestNumber) return;

        const responseDocument = new DOMParser().parseFromString(responseText, "text/html");
        const responsePicker = responseDocument.querySelector("[data-high-variant-picker]");
        if (!responsePicker) throw new Error("The refreshed option picker was missing");

        picker.innerHTML = responsePicker.innerHTML;
        getSelects().forEach((select) => {
          const position = select.dataset.optionPosition || select.dataset.optionIndex;
          const selectedId = selectedByPosition.get(position);
          if (!selectedId) {
            select.value = "";
            return;
          }

          const matchingOption = Array.from(select.options).find((option) => (
            option.dataset.optionValueId === selectedId
          ));
          select.value = matchingOption?.value || "";
        });

        picker.removeAttribute("aria-busy");
        delete form.dataset.personalizationReady;
        initConditionalPersonalization(productPage || document);
        syncPicker({ updateHistory: true, animateMedia: true });

        const replacementSelect = getSelects().find((select) => (
          (select.dataset.optionPosition || select.dataset.optionIndex) === focusPosition
        ));
        replacementSelect?.focus({ preventScroll: true });
      } catch (error) {
        if (error?.name === "AbortError" || activeRequest !== requestNumber) return;
        picker.removeAttribute("aria-busy");
        variantInput.value = "";
        setSubmitState(false, labels.choose);
        if (status) status.textContent = "Options could not be refreshed. Please try that choice again.";
      }
    });

    const requestedOptionValueIds = new URL(window.location.href)
      .searchParams
      .get("option_values")
      ?.split(",")
      .filter(Boolean) || [];
    if (requestedOptionValueIds.length > 0 && !getSelectedVariant()) {
      getSelects().forEach((select) => {
        const matchingOption = Array.from(select.options).find((option) => (
          requestedOptionValueIds.includes(option.dataset.optionValueId)
        ));
        if (matchingOption) select.value = matchingOption.value;
      });
    }

    syncPicker();
  });
}

function initProductOptionPickers(root = document) {
  initHighVariantProductOptionPickers(root);

  root.querySelectorAll("[data-product-variants]").forEach((dataElement) => {
    const form = dataElement.closest("form");
    if (!form || form.dataset.optionPickerReady === "true") return;

    const selects = Array.from(form.querySelectorAll("[data-product-option]"));
    const variantInput = form.querySelector("[data-variant-id]");
    const status = form.querySelector("[data-variant-status]");
    const submitButton = form.querySelector('button[type="submit"]');
    const productCard = form.closest(".product-page__card");
    const productPage = form.closest(".product-page");
    const currentPrice = productCard?.querySelector(".price__current");
    const priceContainer = productCard?.querySelector(".price");
    const isProductClosed = form.dataset.productClosed === "true";
    let variants = [];
    let hasSynced = false;

    try {
      variants = JSON.parse(dataElement.textContent || "[]");
    } catch (_) {
      return;
    }

    if (!selects.length || !variantInput || !submitButton || !variants.length) return;
    form.dataset.optionPickerReady = "true";
    const fallbackPicker = variantInput.closest(".product-form__variant-fallback");
    if (fallbackPicker) {
      fallbackPicker.hidden = true;
      fallbackPicker.setAttribute("aria-hidden", "true");
      variantInput.required = false;
      variantInput.tabIndex = -1;
    }
    selects.forEach((select) => { select.required = true; });

    const labels = {
      add: submitButton.dataset.addLabel || "Add to bag",
      specialOrder: submitButton.dataset.specialOrderLabel || "Add special order to bag",
      choose: submitButton.dataset.chooseLabel || "Choose options",
      unavailable: submitButton.dataset.unavailableLabel || "Unavailable",
      closed: submitButton.dataset.closedLabel || "Ordering closed",
    };

    const updatePrice = (variant) => {
      if (!variant || !currentPrice || !priceContainer) return;
      currentPrice.textContent = variant.price;

      let compare = priceContainer.querySelector(".price__compare");
      let saleNote = priceContainer.querySelector(".price__sale-note");
      if (variant.compareAtPrice) {
        if (!compare) {
          compare = document.createElement("span");
          compare.className = "price__compare";
          priceContainer.appendChild(compare);
        }
        compare.textContent = variant.compareAtPrice;
        compare.hidden = false;
        if (!saleNote) {
          saleNote = document.createElement("span");
          saleNote.className = "price__sale-note";
          saleNote.textContent = "Sale";
          priceContainer.appendChild(saleNote);
        }
        saleNote.hidden = false;
      } else {
        if (compare) compare.hidden = true;
        if (saleNote) saleNote.hidden = true;
      }
    };

    const setSubmitState = (canSubmit, label) => {
      submitButton.dataset.canSubmit = canSubmit ? "true" : "false";
      submitButton.dataset.restingLabel = label;
      if (submitButton.dataset.busy === "true") return;
      submitButton.disabled = !canSubmit;
      submitButton.setAttribute("aria-disabled", canSubmit ? "false" : "true");
      submitButton.textContent = label;
    };

    const syncPicker = () => {
      const selectedValues = selects.map((select) => select.value);

      selects.forEach((select, index) => {
        Array.from(select.options).forEach((option) => {
          if (!option.value) return;

          const canSelect = variants.some((variant) =>
            variant.available &&
            variant.options[index] === option.value &&
            variant.options.slice(0, index).every((value, previousIndex) => (
              !selectedValues[previousIndex] || value === selectedValues[previousIndex]
            ))
          );
          option.disabled = !canSelect;
          option.textContent = `${option.dataset.optionLabel || option.value}${canSelect ? "" : " - Unavailable"}`;
        });
      });

      const missingOptions = selects
        .filter((select) => !select.value)
        .map((select) => select.dataset.optionName || "option");
      const selectedVariant = missingOptions.length === 0
        ? variants.find((variant) => (
          variant.options.length === selectedValues.length &&
          variant.options.every((value, index) => value === selectedValues[index])
        ))
        : null;
      const isSpecialOrder = syncSpecialOrderState(form, selectedVariant);

      variantInput.value = selectedVariant?.id || "";

      if (isProductClosed) {
        setSubmitState(false, labels.closed);
        if (status) status.textContent = "Ordering closed";
      } else if (missingOptions.length > 0) {
        setSubmitState(false, labels.choose);
        if (status) status.textContent = `Choose ${missingOptions.join(", ")}`;
      } else if (!selectedVariant?.available) {
        setSubmitState(false, labels.unavailable);
        if (status) status.textContent = "This combination is unavailable";
      } else {
        setSubmitState(true, isSpecialOrder ? labels.specialOrder : labels.add);
        if (status) {
          status.textContent = isSpecialOrder
            ? `${selectedValues.join(" / ")} · ${form.dataset.outOfStockLabel || "Available to order"}`
            : `${selectedValues.join(" / ")}${form.dataset.mixedAvailability === "true" ? " · In stock" : ""}`;
        }
      }

      if (selectedVariant) {
        updatePrice(selectedVariant);
        if (selectedVariant.mediaId || selectedVariant.mediaUrl) {
          selectProductMedia(productPage, selectedVariant, { animate: hasSynced, scrollMobile: true });
        }
      }

      if (selectedVariant?.id && window.history?.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set("variant", selectedVariant.id);
        window.history.replaceState({}, "", url);
      }

      hasSynced = true;
    };

    selects.forEach((select, index) => select.addEventListener("change", () => {
      selects.slice(index + 1).forEach((followingSelect) => { followingSelect.value = ""; });
      syncPicker();
    }));
    syncPicker();
  });
}

function initConditionalPersonalization(root = document) {
  root.querySelectorAll("form.product-form").forEach((form) => {
    if (form.dataset.personalizationReady === "true") return;

    const panel = form.querySelector("[data-personalization-panel]");
    if (!panel) return;

    const optionSelects = Array.from(form.querySelectorAll("[data-product-option]"));
    const quantityInput = form.querySelector("[data-product-quantity]");
    const quantityNote = form.querySelector("[data-personalization-quantity-note]");
    const personalizationSelect = form.querySelector("[data-personalization-option]") || optionSelects.find((select) => {
      const optionName = (select.dataset.optionName || "").toLowerCase();
      return /^(personalization|personalisation|customization|customisation)$/.test(optionName.trim());
    });

    const isPersonalizationDeclined = (value) => {
      const normalized = String(value || "")
        .toLowerCase()
        .replace(/[’']/g, "")
        .replace(/\s+/g, " ")
        .trim();

      if (["none", "no thanks", "n/a", "not applicable"].includes(normalized)) {
        return true;
      }

      return (
        /\bno (personalization|personalisation|customization|customisation|monogram|embroidery|name)\b/.test(normalized) ||
        /\bnot personali[sz]ed\b/.test(normalized) ||
        /\bcannot be personali[sz]ed\b/.test(normalized) ||
        /\bwithout (personalization|personalisation|customization|customisation|monogram|embroidery|a name)\b/.test(normalized) ||
        /\b(skip|decline) (personalization|personalisation|customization|customisation|monogram|embroidery)\b/.test(normalized)
      );
    };

    // A fixed-wording design can carry its exception in the shopper-facing
    // option label (for example, “13 — Thankful (not personalized)”). This
    // keeps impossible combinations out of Shopify while making the exception
    // obvious in the same dropdown where the customer chooses the design.
    const hasFixedWordingChoice = optionSelects.some((select) =>
      Array.from(select.options).some((option) => isPersonalizationDeclined(option.value)),
    );

    const syncPersonalizationQuantity = (isActive) => {
      if (!quantityInput) return;

      if (quantityInput.dataset.originalMax == null) {
        quantityInput.dataset.originalMax = quantityInput.getAttribute("max") || "";
      }

      if (isActive) {
        quantityInput.max = "1";
        if ((Number.parseInt(quantityInput.value, 10) || 1) > 1) quantityInput.value = "1";
        if (quantityNote) quantityNote.hidden = false;
      } else {
        const originalMax = quantityInput.dataset.originalMax;
        if (originalMax) quantityInput.max = originalMax;
        else quantityInput.removeAttribute("max");
        if (quantityNote) quantityNote.hidden = true;
      }
    };

    // Products that always require personalization have no opt-out option, so
    // their existing required fields should remain untouched.
    if (!personalizationSelect && !hasFixedWordingChoice) {
      form.dataset.personalizationReady = "true";
      syncPersonalizationQuantity(true);
      return;
    }

    form.dataset.personalizationReady = "true";
    const fields = Array.from(panel.querySelectorAll("input, select, textarea"));
    const exclusionNote = form.querySelector("[data-personalization-exclusion-note]");

    const syncPersonalization = () => {
      const isPending = personalizationSelect && !personalizationSelect.value;
      const isDeclined = personalizationSelect
        ? isPersonalizationDeclined(personalizationSelect.value)
        : optionSelects.some((select) => isPersonalizationDeclined(select.value));
      const isActive = !isPending && !isDeclined;
      panel.hidden = !isActive;
      panel.setAttribute("aria-hidden", isActive ? "false" : "true");
      if (exclusionNote) exclusionNote.hidden = !isDeclined;

      fields.forEach((field) => {
        field.disabled = !isActive;
        const isRequiredWhenActive = field.dataset.personalizationRequired === "true";
        field.required = isActive && isRequiredWhenActive;
        if (isRequiredWhenActive) {
          field.setAttribute("aria-required", isActive ? "true" : "false");
        }
      });

      syncPersonalizationQuantity(isActive);
    };

    optionSelects.forEach((select) => select.addEventListener("change", syncPersonalization));
    syncPersonalization();
  });
}

function initProductForms(root = document) {
  root.querySelectorAll("form.product-form").forEach((form) => {
    if (form.dataset.productFormReady === "true") return;

    const submitButton = form.querySelector('button[type="submit"]');
    if (!submitButton) return;

    const message = form.querySelector("[data-product-form-message]");
    const isProductClosed = form.dataset.productClosed === "true";
    form.dataset.productFormReady = "true";
    // Native constraint validation runs before the submit event, which would
    // prevent Review timing from opening when personalization is still blank.
    // The handler below validates explicitly after the timing step instead.
    form.noValidate = true;

    if (!submitButton.dataset.restingLabel) {
      submitButton.dataset.restingLabel = submitButton.textContent.trim() || "Add to bag";
    }
    if (!submitButton.dataset.canSubmit) {
      submitButton.dataset.canSubmit = (!isProductClosed && !submitButton.disabled) ? "true" : "false";
    }
    if (form.querySelector("[data-purchase-confirmation]")) {
      form.dataset.enhancedConfirmation = "true";
      concealPurchaseConfirmations(form);
    }

    const clearMessage = () => {
      if (!message || message.hidden) return;
      message.hidden = true;
      message.textContent = "";
      message.setAttribute("role", "status");
    };

    const showError = (text) => {
      if (!message) return;
      message.textContent = text;
      message.hidden = false;
      message.setAttribute("role", "alert");
      message.focus({ preventScroll: true });
    };

    form.addEventListener("input", clearMessage);
    form.addEventListener("change", clearMessage);

    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (submitButton.dataset.busy === "true") return;

      if (isProductClosed) {
        showError("This ordering window has closed, so this item can no longer be added to the bag.");
        return;
      }

      const variantInput = form.querySelector('[name="id"]');
      if (!variantInput?.value) {
        showError("Choose each product option before adding this item.");
        form.querySelector("[data-product-option]")?.focus({ preventScroll: true });
        return;
      }

      const personalizationPanel = form.querySelector("[data-personalization-panel]");
      const quantityInput = form.querySelector("[data-product-quantity]");
      const personalizationIsActive = personalizationPanel && !personalizationPanel.hidden;
      if (personalizationIsActive && (Number.parseInt(quantityInput?.value, 10) || 1) > 1) {
        if (quantityInput) quantityInput.value = "1";
        showError("Add personalized pieces one at a time so each item's details stay attached correctly.");
        quantityInput?.focus({ preventScroll: true });
        return;
      }

      // Let shoppers see the timing explanation before validating unrelated
      // personalization fields. Otherwise Safari only focuses the empty field,
      // which makes the Review timing button appear to do nothing.
      if (revealPurchaseConfirmations(form, submitButton)) return;

      if (!form.checkValidity()) {
        const missingPersonalization = form.querySelector("[data-personalization-required]:invalid");
        if (missingPersonalization) {
          showError("Complete each required personalization field marked with * before adding this item.");
        }
        form.reportValidity();
        return;
      }

      acceptPurchaseConfirmations(form);
      const formData = new FormData(form);

      clearMessage();
      submitButton.dataset.busy = "true";
      submitButton.disabled = true;
      submitButton.setAttribute("aria-disabled", "true");
      submitButton.setAttribute("aria-busy", "true");
      submitButton.textContent = submitButton.dataset.busyLabel || "Adding…";

      try {
        const actionUrl = new URL(form.action, window.location.href);
        actionUrl.pathname = actionUrl.pathname.replace(/\/cart\/add(?:\.js)?\/?$/, "/cart/add.js");
        const response = await fetch(actionUrl.toString(), {
          method: "POST",
          body: formData,
          credentials: "same-origin",
          headers: { Accept: "application/json" },
        });
        const payload = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(payload.description || payload.message || "This item could not be added. Please review your choices and try again.");
        }

        submitButton.textContent = "Added to bag";
        const shopRoot = window.Shopify?.routes?.root || "/";
        window.location.assign(`${shopRoot}cart`);
      } catch (error) {
        submitButton.dataset.busy = "false";
        submitButton.removeAttribute("aria-busy");
        const canSubmit = submitButton.dataset.canSubmit === "true";
        submitButton.disabled = !canSubmit;
        submitButton.setAttribute("aria-disabled", canSubmit ? "false" : "true");
        submitButton.textContent = form.dataset.purchaseConfirmationRevealed === "true"
          ? (submitButton.dataset.confirmLabel || "I understand — add to bag")
          : (submitButton.dataset.restingLabel || submitButton.dataset.addLabel || "Add to bag");
        showError(error instanceof Error ? error.message : "This item could not be added. Please try again.");
      }
    });
  });
}

// When the merchant picks "Direct link" for checkout, the button is an <a>,
// not a submit button — so quantity edits would be lost. Before navigating,
// POST any pending quantity changes via /cart/update.js so the checkout URL
// sees the latest cart.
function initCartDirectLinkSync() {
  const link = document.querySelector("[data-cart-checkout-link]");
  if (!link) return;

  link.addEventListener("click", async (e) => {
    const form = document.getElementById("cart");
    if (!form) return;

    if (!form.checkValidity()) {
      e.preventDefault();
      form.reportValidity();
      return;
    }

    const inputs = form.querySelectorAll("[data-cart-quantity]");
    if (inputs.length === 0) return;

    const updates = {};
    let hasChanges = false;
    inputs.forEach((input) => {
      const key = input.dataset.key;
      const qty = Math.max(0, parseInt(input.value, 10) || 0);
      if (key && qty !== parseInt(input.defaultValue, 10)) {
        updates[key] = qty;
        hasChanges = true;
      }
    });

    if (!hasChanges) return;

    e.preventDefault();
    try {
      const shopRoot = window.Shopify?.routes?.root || "/";
      const localizedRoot = shopRoot.endsWith("/") ? shopRoot : `${shopRoot}/`;
      await fetch(`${localizedRoot}cart/update.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ updates }),
      });
    } catch (_) {
      /* proceed even on failure — better to reach checkout than block */
    }
    window.location.href = link.href;
  });
}
